import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Users, Calendar, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const tcMatches = [
  { tour: 1, date: '29.08', time: null,    home: 'Cheseaux 2',      away: 'Cheyres-Châbles', score: '3:3' },
  { tour: 1, date: '24.08', time: null,    home: 'Grandson 2',      away: 'Bussigny 1',      score: '0:6' },
  { tour: 2, date: '06.09', time: '9h00',  home: 'Cheseaux 2',      away: 'Grandson 2',      score: '0:0' },
  { tour: 2, date: '05.09', time: null,    home: 'Bussigny 1',      away: 'Cheyres-Châbles', score: '6:0' },
  { tour: 3, date: '11.09', time: '18h00', home: 'Cheyres-Châbles', away: 'Grandson 2',      score: '0:0' },
  { tour: 3, date: '12.09', time: '9h00',  home: 'Bussigny 1',      away: 'Cheseaux 2',      score: '0:0' },
];

const tcStandings = [
  { rank: 1, team: 'Bussigny 1',      pts: 12, sets: '24 - 1' },
  { rank: 2, team: 'Cheseaux 2',      pts: 3,  sets: '9 - 6' },
  { rank: 3, team: 'Cheyres-Châbles', pts: 3,  sets: '6 - 21' },
  { rank: 4, team: 'Grandson 2',      pts: 0,  sets: '1 - 12' },
];

const TeamChallengePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header scrollY={100} />

      <div className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="team-challenge-back-link"
            className="inline-flex items-center text-green-600 hover:text-green-700 mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Link>

          {/* Hero */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Nouveauté 2026
            </div>
            <h1 data-testid="team-challenge-title" className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Team Challenge
            </h1>
            <p className="text-xl text-gray-600">
              Une grande première pour le TC Cheyres-Châbles !
            </p>
          </div>

          {/* Carte présentation équipe */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl mb-10 border border-green-100">
            <div className="flex items-center mb-6">
              <Trophy className="w-12 h-12 text-green-600 mr-4" />
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Notre équipe 2026</h2>
                <p className="text-green-700 font-semibold">Première participation</p>
              </div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              Pour la <strong>toute première fois</strong>, le TC Cheyres-Châbles engage une équipe
              dans le championnat <strong>Team Challenge</strong>. Notre équipe représentera
              le club en <strong>2ème ligue Hommes</strong>.
            </p>
            <div className="mt-6 inline-flex items-center bg-white px-5 py-3 rounded-xl shadow-sm">
              <Users className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Catégorie</p>
                <p className="font-bold text-gray-900">2ème Ligue Hommes</p>
              </div>
            </div>
          </div>

          {/* Format */}
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Format de la compétition</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border-2 border-blue-100 rounded-2xl p-6 hover:border-blue-300 transition-colors">
                <div className="text-5xl font-extrabold text-blue-600 mb-2">4</div>
                <p className="text-lg font-bold text-gray-900 mb-1">Simples</p>
                <p className="text-gray-600 text-sm">
                  Quatre matchs de simple opposant les joueurs de chaque équipe.
                </p>
              </div>
              <div className="bg-white border-2 border-purple-100 rounded-2xl p-6 hover:border-purple-300 transition-colors">
                <div className="text-5xl font-extrabold text-purple-600 mb-2">2</div>
                <p className="text-lg font-bold text-gray-900 mb-1">Doubles</p>
                <p className="text-gray-600 text-sm">
                  Deux matchs de double pour clôturer la rencontre.
                </p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-4 italic">
              Chaque rencontre se joue donc en 6 matchs au total : 4 simples + 2 doubles.
            </p>
          </div>

          {/* Programme + Classement */}
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <Calendar className="w-7 h-7 text-green-600 mr-3" />
              2L Messieurs - Groupe 22
            </h2>

            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Résultats</h3>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center text-gray-600">Tour</th>
                        <th className="px-3 py-2 text-center text-gray-600">Date</th>
                        <th className="px-3 py-2 text-left text-gray-600">Rencontre</th>
                        <th className="px-3 py-2 text-center text-gray-600">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tcMatches.map((m, idx) => {
                        const hasCheyres = m.home.includes('Cheyres') || m.away.includes('Cheyres');
                        return (
                          <tr key={idx} className={hasCheyres ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                            <td className="px-3 py-2.5 text-center">{m.tour}</td>
                            <td className="px-3 py-2.5 text-center">{m.time ? `${m.date} - ${m.time}` : m.date}</td>
                            <td className={`px-3 py-2.5 ${hasCheyres ? 'font-semibold text-green-700' : ''}`}>
                              {m.home} - {m.away}
                            </td>
                            <td className="px-3 py-2.5 text-center font-semibold">{m.score}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Classement</h3>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center text-gray-600">Rang</th>
                        <th className="px-3 py-2 text-left text-gray-600">Équipe</th>
                        <th className="px-3 py-2 text-center text-gray-600">PT</th>
                        <th className="px-3 py-2 text-center text-gray-600">SET</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tcStandings.map((row, idx) => {
                        const isCheyres = row.team.includes('Cheyres');
                        return (
                          <tr key={idx} className={isCheyres ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                            <td className="px-3 py-2.5 text-center font-semibold">{row.rank}</td>
                            <td className={`px-3 py-2.5 ${isCheyres ? 'font-bold text-green-700' : ''}`}>{row.team}</td>
                            <td className="px-3 py-2.5 text-center">{row.pts}</td>
                            <td className="px-3 py-2.5 text-center">{row.sets}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm mt-4 italic">
              Les rencontres se jouent en août et septembre 2026. Les scores et le classement seront mis à jour au fil des tours.
            </p>
          </div>

          {/* Bonne chance */}
          <div className="bg-gray-900 text-white rounded-3xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Allez Cheyres-Châbles ! 🎾</h3>
            <p className="text-gray-300">
              Soutenez l'équipe lors des rencontres à domicile. Venez nombreux encourager nos joueurs pour cette aventure inédite !
            </p>
          </div>
        </div>
      </div>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />

      <Footer />
    </div>
  );
};

export default TeamChallengePage;
